import React, { useState, useReducer, ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { text, unitList, unitMap, gearForceColumns } from '../data'; // global
import { gearTypes, handList, torqueOrPowerOption, gearDriveOption, directionRotationOption, gearApexOption, errorMessages, formText, resultsCopy, additionalText } from './data'; // component specific data
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import { SelectOption } from '../types/types';
import NumberInput from '../../components/bearing-search/components/forms/NumberInput';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import { breakCamelcaseStrings, toggleNextTools, getTooltip, toggleReportReady, toggleImportantNote } from '../utils/engineering-tools';
import { GearForceResultData } from './types';
import gearForceReducer, {
	initialGearForceState,
	mapFieldsPerGearType,
	getRequiredFieldsByGearType,
	handFieldLabelPerGearType,
	mapDriveOptionsPerGearType,
} from './reducer';
import { TimkenUnit, GearForcePayload } from '../../components/bearing-search/types/types';
import { getGearLoadsCalculation } from '../../components/bearing-search/api/api';
import ResultsTable from '../components/ResultsTable';
import GearVideo from './fragments/GearVideo';
import {
	trackStart,
	trackGearForceResults,
} from '../utils/analytics';

const GearForce: React.FC = () => {
	const [ formState, dispatch ] = useReducer(
		gearForceReducer,
		initialGearForceState,
	);

	const [ isValidated, setIsValidated ] = useState<boolean>(false); // has form been validated
	const [ hasValidGearType, setHasValidGearType ] = useState<boolean>(false);

	const [ results, setResults ] = useState<GearForceResultData | null>(null);
	const [ displayResults, setDisplayResults ] = useState<boolean>(false);
	const [ isResultsLoading, setIsResultsLoading ] = useState<boolean>(false); // while API call
	const [ isSubmitPending, setIsSubmitPending ] = useState(false); // flag to indicate waiting on validation
	const [ powerFactorOptions, setPowerFactorOptions ] = useState(gearDriveOption);
	const [ gearImageBytes, setGearImageBytes ] = useState('');
	const [ isMobile, setIsMobile ] = useState<boolean>(false);

	const resultsRef = useRef<HTMLDivElement>(null);

	/**
	 * Check if field is displayed for the selected gearType
	 *
	 * @param {string} fieldName - The name of the field to check.
	 */
	const displayField = (fieldName: string) => {
		if (formState.gearType == '') {
			return;
		} // return if no gearType selected

		const gearType = Number(formState.gearType);
		const validGearTypes = mapFieldsPerGearType[ fieldName ];

		return validGearTypes?.includes(gearType);
	};

	/**
	 * Prepare Label for Hand input as there may be a different name based on Gear Type
	 */
	const prepareHandLabel = () => {
		if (formState.gearType === '') {
			return;
		}

		return handFieldLabelPerGearType[ formState.gearType ];
	};

	useEffect(() => {
		trackStart('gearForceCalculations');
		setIsMobile(window.matchMedia("(max-width: 768px)").matches);
	}, []);

	/**
	 * Validate gearType selected option
	 */
	useEffect(() => {
		const validGearType = formState.gearType !== '' && Number(formState.gearType) >= 0 && Number(formState.gearType) <= 11;
		setHasValidGearType(validGearType);
	}, [ formState.gearType ]);

	/**
	 * Construct Timken API GearForcePayload
	 */
	const constructGearPayload = (): GearForcePayload => {
		const requiredFields = getRequiredFieldsByGearType(formState.gearType ?? '');
		const payload: GearForcePayload = {};

		const gearDimensionFields = new Set([
			'apex',
			'centerDistance',
			'concaveAngle',
			'convexAngle',
			'efficiency',
			'faceAngle',
			'hand',
			'helixAngle',
			'leadAngle',
			'meshAngle',
			'pitchAngle',
			'pressureAngle',
			'ratio',
			'rootAngle',
			'spiralAngle',
			'workingDiameter',
		]);

		const gearDimension: Record<string, number> = {};

		for (const field of requiredFields) {
			switch (field) {
				case 'unit':
					payload.unit = formState.unit;
					break;
				case 'gearType':
					payload.gearTypeKey = Number(formState.gearType);
					break;
				case 'gearDriveOptions':
					payload.gearDriveOptions = formState.gearDriveOptions;
					break;
				case 'torque':
					payload.torque = Number(formState.torque);
					break;
				case 'speed':
					payload.operatingSpeed = Number(formState.speed);
					break;
				case 'directionRotation':
					payload.gearRotation = Number(formState.directionRotation);
					break;
				case 'torqueOrPower':
					payload.torqueorPower = Number(formState.torqueOrPower);
					break;
				default:
					if (gearDimensionFields.has(field)) {
						gearDimension[ field ] = Number(formState[ field ]);
					}
					break;
			}
		}

		if (Object.keys(gearDimension).length > 0) {
			payload.gearDimension = gearDimension;
		}

		// Add in Gear Name to Payload Analytic purposes if it exists
		if ( formState.gearName ) {
			payload.gearName = DOMPurify.sanitize(formState.gearName);
		}

		return payload;
	};

	/**
	 * format Calculation Results Data
	 *
	 * @param  apiResponse
	 */
	const formatCalculationResults = (apiResponse) => {
		let reportData: GearForceResultData | null = null;

		reportData = {
			sectionHeading: gearForceColumns,
			sectionData: [
				[
					text.tangentialWithUnit,
					apiResponse.tangentialLoads.toString(),
					unitMap[ formState.unit ].force,
				],
				[
					text.separatingWithUnit,
					apiResponse.separatingLoads.toString(),
					unitMap[ formState.unit ].force,
				],
				[
					text.axialWithUnit,
					apiResponse.axialLoads.toString(),
					unitMap[ formState.unit ].force,
				],
			],
		};

		return reportData;
	};

	/**
	 * Show disclaimer link if results are displayed
	 */
	useEffect(() => {
		toggleImportantNote(!! results);
		toggleNextTools(!! results);
		setDisplayResults(!! results);
		toggleReportReady( !! results, null, null, 'Gear');

		return () => {
			toggleImportantNote(false);
			toggleNextTools(false);
			setDisplayResults(false);
		};
	}, [ results ]);

	/**
	 * scroll to Results
	 */
	useEffect(() => {
		if (displayResults) {
			resultsRef.current?.scrollIntoView({
				behavior: 'smooth',
			});
		}
	}, [ displayResults ]);

	useEffect(() => {
		if (isSubmitPending) {
			const isValid = [ ...formState.validation.values() ].every((valid) => valid === true);

			if (isValid) {
				const payload = constructGearPayload();
				callAPI(payload);
			}

			setIsValidated(true);
			setIsSubmitPending(false); // reset submit attempt
		}
	}, [ formState.validation ]);

	/**
	 * @param  payload
	 */
	const callAPI = async (payload: GearForcePayload) => {
		setIsResultsLoading(true);
		setDisplayResults(true); // display loading table animation

		try {
			const response = await getGearLoadsCalculation(payload);
			const formatResults = formatCalculationResults(response);

			// analytics calculations data
			const numberOfResults = response ? 1 : 0;
			trackGearForceResults('gearForceCalculations', payload, numberOfResults, '');
			setResults(formatResults);
			setGearImageBytes(response.gearImageBytes.toString());
		} catch (error) {
			console.error('Conversion API failed', error);
		}

		setIsResultsLoading(false);
	};

	/**
	 * @param  event
	 */
	const handleSubmit = (event: FormEvent) => {
		event.preventDefault();

		dispatch({
			type: 'validate_fields',
		});

		setIsSubmitPending(true);
	};

	/***/
	const clearFormAndResults = () => {
		setIsValidated(false);
		setResults(null);
	};

	/***/
	const handleFormChange = () => {
		clearFormAndResults();
	};

	/**
	 * Reset form to initial state
	 */
	const handleFormReset = () => {
		clearFormAndResults();

		dispatch({
			type: 'reset',
			payload: initialGearForceState,
		});
	};

	const handleGearTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
		dispatch({
			type: 'change_gear_type',
			payload: event.target.value,
		});
		updateGearDriveOptions(Number(event.target.value));
	};

	const updateGearDriveOptions = (value: number) => {
		const driveOptions = structuredClone(gearDriveOption);
		const driveOptionsPerGearType = structuredClone(mapDriveOptionsPerGearType);

		if (!driveOptionsPerGearType['driver'].includes(value)) {
			driveOptions.pop();
		}
		if (!driveOptionsPerGearType['driven'].includes(value)) {
			driveOptions.shift();
		}
		setPowerFactorOptions(driveOptions);

		// If only Driven or Driver is available, pre-select the value for the drop-down
		if (driveOptions.length == 1) {
			dispatch({
				type: 'update_value',
				payload: { gearDriveOptions: driveOptions[0].value },
			});
		}
	};

	return (
		<>
			<form onSubmit={handleSubmit} onChange={handleFormChange}>
				<div className="row form-row">
					<div className="col-12 col-md-9">
						<div className="row gx-4">

							<div className="col-12 col-md-4">
								<SelectDropdown
									id="gear_type"
									name="gear_type"
									required={true}
									label="Gear Type"
									value={formState.gearType}
									placeholder={formText.selectOne}
									options={gearTypes.map((name, index) => {
										return {
											id: `gear_type_${ index }`,
											value: index,
											label: breakCamelcaseStrings(name),
										} as SelectOption<number>;
									})}
									onChange={handleGearTypeChange}
								/>
							</div>

							{/* GearType video */}
							{isMobile &&  hasValidGearType &&
                <div className="col-12 col-md-3 d-md-none">
                	<GearVideo gearTypeId={formState.gearType ?? ''} />
                </div>
							}

							<div className="col-12 col-md-4">
								<TextInput
									id="gear_name"
									required={false}
									label={formText.gearName}
									name="gear_name"
									value={formState.gearName}
									tooltip={getTooltip(formText.gearName)}
									onChange={(e) =>
										dispatch({
											type: 'update_value',
											payload: { gearName: e.target.value },
										})
									}
								/>
							</div>

							<div className="col-12 col-md-4">
								<SelectDropdown
									id="unit"
									name="unit"
									required={true}
									label={text.displayUnits}
									value={formState.unit}
									options={unitList}
									onChange={(event: ChangeEvent<HTMLSelectElement>) =>
										dispatch({
											type: 'change_unit',
											payload: event.target.value as TimkenUnit,
										})
									}
								/>
							</div>

							<div className="col-12 col-md-4">
								<NumberInput
									id="mean_working_diameter"
									label="Mean Working Diameter"
									unitLabel={unitMap[ formState.unit ].length}
									value={formState.workingDiameter}
									required={true}
									invalid={
										isValidated &&
                    ! formState.validation.get('workingDiameter')
									}
									errorMessage={errorMessages.positiveNumber}
									onChange={(e) =>
										dispatch({
											type: 'update_value',
											payload: { workingDiameter: e.target.value },
										})
									}
									onFocus={(e) => e.target.select()}
								/>
							</div>

							<div className="col-12 col-md-4">
								<NumberInput
									id="mesh_angle"
									label="Mesh Angle"
									unitLabel={formText.angleDeg}
									value={formState.meshAngle}
									min={-360}
									max={360}
									required={true}
									invalid={
										isValidated &&
                    ! formState.validation.get('meshAngle')
									}
									errorMessage={errorMessages.angle}
									onChange={(e) =>
										dispatch({
											type: 'update_value',
											payload: { meshAngle: e.target.value },
										})
									}
									onFocus={(e) => e.target.select()}
								/>
							</div>

							{displayField('pressureAngle') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="pressure_angle"
                		label="Pressure Angle"
                		unitLabel={formText.angleDeg}
                		value={formState.pressureAngle}
                		min={15}
                		max={35}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('pressureAngle')
                		}
                		errorMessage={errorMessages.pressureAngle}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { pressureAngle: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('helixAngle') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="helix_angle"
                		label="Helix Angle"
                		unitLabel={formText.angleDeg}
                		value={formState.helixAngle}
                		min={1}
                		max={360}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('helixAngle')
                		}
                		errorMessage={errorMessages.anglePositive}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { helixAngle: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('pitchAngle') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="pitch_angle"
                		label="Pitch Angle"
                		unitLabel={formText.angleDeg}
                		value={formState.pitchAngle}
                		min={-360}
                		max={360}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('pitchAngle')
                		}
                		errorMessage={errorMessages.angle}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { pitchAngle: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('leadAngle') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="lead_angle"
                		label="Lead Angle"
                		unitLabel={formText.angleDeg}
                		value={formState.leadAngle}
                		min={1}
                		max={360}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('leadAngle')
                		}
                		errorMessage={errorMessages.anglePositive}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { leadAngle: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('spiralAngle') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="spiral_angle"
                		label="Spiral Angle"
                		unitLabel={formText.angleDeg}
                		value={formState.spiralAngle}
                		min={1}
                		max={360}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('spiralAngle')
                		}
                		errorMessage={errorMessages.anglePositive}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { spiralAngle: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('rootAngle') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="root_angle"
                		label="Root Angle"
                		unitLabel={formText.angleDeg}
                		value={formState.rootAngle}
                		min={1}
                		max={360}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('rootAngle')
                		}
                		errorMessage={errorMessages.anglePositive}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { rootAngle: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('faceAngle') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="face_angle"
                		label="Face Angle"
                		unitLabel={formText.angleDeg}
                		value={formState.faceAngle}
                		min={1}
                		max={360}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('faceAngle')
                		}
                		errorMessage={errorMessages.anglePositive}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { faceAngle: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('efficiency') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="gearing_efficiency"
                		label="Gearing Efficiency %"
                		unitLabel="%"
                		value={formState.efficiency}
                		min={0}
                		max={100}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('efficiency')
                		}
                		errorMessage={errorMessages.percentage}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { efficiency: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('ratio') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="ratio"
                		label="Ratio"
                		unitLabel=""
                		value={formState.ratio}
                		min={0}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('ratio')
                		}
                		errorMessage={errorMessages.positiveNumber}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { ratio: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('hand') &&
                <div className="col-12 col-md-4">
                	<SelectDropdown
                		id="hand"
                		name="hand"
                		required={true}
                		label={prepareHandLabel()}
                		value={formState.hand}
                		options={handList}
                		placeholder={formText.selectOne}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('hand')
                		}
                		errorMessage={errorMessages.required}
                		onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                			dispatch({
                				type: 'update_value',
                				payload: { hand: e.target.value },
                			})
                		}
                	/>
                </div>
							}

							{displayField('apex') &&
                <div className="col-12 col-md-4">
                	<SelectDropdown
                		id="gear_apex"
                		name="gear_apex"
                		required={true}
                		label='Gear Apex'
                		value={formState.apex}
                		options={gearApexOption}
                		placeholder={formText.selectOne}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('apex')
                		}
                		errorMessage={errorMessages.required}
                		onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                			dispatch({
                				type: 'update_value',
                				payload: { apex: e.target.value },
                			})
                		}
                	/>
                </div>
							}

							{displayField('centerDistance') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="center_distance"
                		label="Center Distance"
                		unitLabel={unitMap[ formState.unit ].length}
                		value={formState.centerDistance}
                		min={0}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('centerDistance')
                		}
                		errorMessage={errorMessages.centerDistance}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { centerDistance: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('convexAngle') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="convex_pressure_angle"
                		label="Convex Pressure Angle"
                		unitLabel={formText.angleDeg}
                		value={formState.convexAngle}
                		min={1}
                		max={360}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('convexAngle')
                		}
                		errorMessage={errorMessages.anglePositive}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { convexAngle: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{displayField('concaveAngle') &&
                <div className="col-12 col-md-4">
                	<NumberInput
                		id="concave_pressure_angle"
                		label="Concave Pressure Angle"
                		unitLabel={formText.angleDeg}
                		value={formState.concaveAngle}
                		min={1}
                		max={360}
                		required={true}
                		invalid={
                			isValidated &&
                      ! formState.validation.get('concaveAngle')
                		}
                		errorMessage={errorMessages.anglePositive}
                		onChange={(e) =>
                			dispatch({
                				type: 'update_value',
                				payload: { concaveAngle: e.target.value },
                			})
                		}
                		onFocus={(e) => e.target.select()}
                	/>
                </div>
							}

							{hasValidGearType &&
                <div className="col-12">
                	<div className="t-engineering-tools__options d-flex d-block-md">
                		{torqueOrPowerOption.map((option) => {
                			return (
                				<div className="m-radio" key={option.id}>
                					<div className="m-radio__field">
                						<input
                							id={option.id}
                							className="m-radio__input"
                							checked={formState.torqueOrPower === option.value}
                							type="radio"
                							value={option.value}
                							onChange={() =>
                								dispatch({
                									type: 'update_value',
                									payload: { torqueOrPower: option.value },
                								})
                							}
                						/>
                						<label className="m-radio__label" htmlFor={option.id}>
                							{option.label}
                						</label>
                					</div>
                				</div>
                			);
                		})}
                	</div>
                </div>
							}

						</div>
					</div>

					{!isMobile && hasValidGearType &&
            <div className="col-12 col-md-3 d-none d-md-block">
            	<GearVideo gearTypeId={formState.gearType ?? ''} />
            </div>
					}

					{hasValidGearType &&
            <div className="col-12">
            	<div className="row gx-4">

            		{formState.torqueOrPower == '2' &&
                  <div className="col-12 col-md-3">
                  	<NumberInput
                  		id="torque"
                  		label="Torque"
                  		required={true}
                  		unitLabel={unitMap[ formState.unit ].torque}
                  		value={formState.torque}
                  		min={1}
                  		invalid={
                  			isValidated &&
                        ! formState.validation.get('torque')
                  		}
                  		errorMessage={errorMessages.positiveNumber}
                  		onChange={(e) =>
                  			dispatch({
                  				type: 'update_value',
                  				payload: { torque: e.target.value },
                  			})
                  		}
                  		onFocus={(e) => e.target.select()}
                  	/>
                  </div>
            		}

            		{formState.torqueOrPower == '1' &&
                  <div className="col-12 col-md-3">
                  	<NumberInput
                  		id="power"
                  		label="Power"
                  		required={true}
                  		unitLabel={unitMap[ formState.unit ].power}
                  		value={formState.torque} // power or torque are both assigned torque in the payload
                  		min={1}
                  		invalid={
                  			isValidated &&
                        ! formState.validation.get('torque')
                  		}
                  		errorMessage={errorMessages.positiveNumber}
                  		onChange={(e) =>
                  			dispatch({
                  				type: 'update_value',
                  				payload: { torque: e.target.value },
                  			})
                  		}
                  		onFocus={(e) => e.target.select()}
                  	/>
                  </div>
            		}

            		<div className="col-12 col-md-3">
            			<NumberInput
            				id="speed"
            				label="Speed"
            				required={true}
            				unitLabel="rpm"
            				value={formState.speed}
            				min={1}
            				invalid={
            					isValidated &&
                      ! formState.validation.get('speed')
            				}
            				errorMessage={errorMessages.positiveNumber}
            				onChange={(e) =>
            					dispatch({
            						type: 'update_value',
            						payload: { speed: e.target.value },
            					})
            				}
            				onFocus={(e) => e.target.select()}
            			/>
            		</div>

            		<div className="col-12 col-md-3">
            			<SelectDropdown
            				id="power_factor"
            				name="power_factor"
            				required={true}
            				label='Power Factor'
            				value={formState.gearDriveOptions}
            				options={powerFactorOptions}
            				placeholder={formText.selectOne}
            				invalid={
            					isValidated &&
                      ! formState.validation.get('gearDriveOptions')
            				}
            				errorMessage={errorMessages.required}
            				onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            					dispatch({
            						type: 'update_value',
            						payload: { gearDriveOptions: e.target.value },
            					})
            				}
            			/>
            		</div>

            		<div className="col-12 col-md-3">
            			<SelectDropdown
            				id="direction_rotation"
            				name="direction_rotation"
            				required={true}
            				label='Direction of Rotation'
            				value={formState.directionRotation}
            				options={directionRotationOption}
            				placeholder={formText.selectOne}
            				invalid={
            					isValidated &&
                      ! formState.validation.get('directionRotation')
            				}
            				errorMessage={errorMessages.required}
            				onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            					dispatch({
            						type: 'update_value',
            						payload: { directionRotation: e.target.value },
            					})
            				}
            			/>
            		</div>

            	</div>
            </div>
					}

					<div className="col-12 mt-2">
						<div className="t-engineering-tools__terms-of-use"
							dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
						/>
						<div className="t-engineering-tools__search-actions">
							<button
								className="btn btn--primary"
								type="submit"
								disabled={! hasValidGearType}
							>
								{text.calculate}
							</button>

							<button
								className="btn btn--secondary"
								type="button"
								onClick={handleFormReset}
							>
								{text.clear}
							</button>
						</div>
					</div>
				</div>
			</form>

			{/* Results Section */}
			{displayResults &&
		  <div className="t-engineering-tools__results" ref={resultsRef}>
			  <h2>{text.results}</h2>
			  {! isResultsLoading &&
				  <>
					  <p className="t-engineering-tools__results-link d-none">
						  <a href="#">{text.disclaimer}</a>
					  </p>
					  {additionalText && (
						  <div dangerouslySetInnerHTML={{ __html: additionalText }}/>
					  )}
					  <p
						  className="t-engineering-tools__results-description"
						  dangerouslySetInnerHTML={{ __html: resultsCopy.headingDescription }}
					  />
					  <h3>
						  {resultsCopy.tableTitle}
					  </h3>
				  </>

			  }
			  <div className="table-and-image-container">
				  <div>
					  <ResultsTable
						  table={results}
						  loading={isResultsLoading}
						  classNames="c-bearings-table"
					  />
				  </div>
				  { ! isResultsLoading && (
					  <div className="gear-force-image">
						  <img id="gear_force_graph" src={`data:image/jpg;base64, ${gearImageBytes}`}
							   alt="Gear Force Graph"/>
					  </div>
				  )}
			  </div>
		  </div>
			}
		</>
	);
};

export default GearForce;
